import { useRef, useState } from 'react'
import { usePresignUpload, type UploadContext } from '../../hooks/useChurchAdmin'
import { CropModal } from './CropModal'

interface ImageUploadProps {
  context: UploadContext
  onUpload: (publicUrl: string) => void
  children: React.ReactNode
  className?: string
  maxSizeMb?: number
  /** Override the crop aspect ratio (width / height) for this context. */
  aspect?: number
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'] as const
const ACCEPT_ATTR = ACCEPTED.join(',')

const MAX_DIM: Record<UploadContext, number> = {
  'event-image': 1600,
  'church-cover': 1600,
  'church-logo': 512,
  'user-avatar': 512,
}

// Crop aspect ratio (width / height) per upload context, chosen to match how
// each image is displayed so nothing important gets cut off by object-cover.
const ASPECT: Record<UploadContext, number> = {
  'event-image': 3 / 2,
  'church-cover': 16 / 9,
  'church-logo': 1,
  'user-avatar': 1,
}

export function ImageUpload({
  context, onUpload, children, className, maxSizeMb = 5, aspect,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const presign = usePresignUpload()
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [cropSrc, setCropSrc] = useState<{ url: string; name: string } | null>(null)

  const isPending = presign.isPending || uploading

  function selectFile(file: File) {
    setError(null)

    if (!ACCEPTED.includes(file.type as typeof ACCEPTED[number])) {
      setError('Only JPEG, PNG, or WebP images are allowed.')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('Image must be under 20 MB.')
      return
    }

    setCropSrc({ url: URL.createObjectURL(file), name: file.name })
  }

  function closeCrop() {
    if (cropSrc) URL.revokeObjectURL(cropSrc.url)
    setCropSrc(null)
  }

  async function uploadCropped(file: File) {
    closeCrop()

    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`Image must be under ${maxSizeMb} MB. Try zooming out or a smaller image.`)
      return
    }

    setUploading(true)
    try {
      const { data } = await presign.mutateAsync({ context, contentType: file.type })
      await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })
      onUpload(data.publicUrl)
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) selectFile(file)
          e.target.value = ''
        }}
      />
      <div
        onClick={() => !isPending && inputRef.current?.click()}
        style={{ cursor: isPending ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
      >
        {children}
      </div>
      {error && (
        <p className="mt-1.5 text-[11.5px] text-warn">{error}</p>
      )}
      {cropSrc && (
        <CropModal
          imageSrc={cropSrc.url}
          aspect={aspect ?? ASPECT[context]}
          maxDim={MAX_DIM[context]}
          fileName={cropSrc.name}
          onCancel={closeCrop}
          onApply={(file) => void uploadCropped(file)}
        />
      )}
    </div>
  )
}
